import React, { useEffect, useRef, useState } from 'react'
import {
  Modal,
  Button,
  Icon,
  Card,
  Avatar,
  Input,
  Badge,
  Tooltip,
  Select,
  Form
} from 'antd'
import axios from 'axios'
import { message } from 'antd'
import {
  FiActivity,
  FiX,
  FiAlertTriangle,
  FiEdit2,
  FiCheckCircle,
  FiEye,
  FiPlus,
  FiSave,
  FiCheck
} from 'react-icons/fi'
import { config } from '../../../../../constants'
import { useTranslation } from 'react-i18next'

const { Option } = Select
const { TextArea } = Input

const BASE_URL_COLLABORA = config.questionAnswerAiUrl

const CollaboraEditorModal = ({
  orgUserList,
  visible,
  onClose,
  documentName,
  documentId,
  userId,
  userName,
  shlToken,
  title = 'Document Editor',
  isEdit = false,
  isView = false,
  orgId, // ADD THIS
}) => {
  const iframeRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [debugInfo, setDebugInfo] = useState('')
  const [workflowVisible, setWorkflowVisible] = useState(false)
  const [documentData, setDocumentData] = useState(null)

  // Workflow state
  const [editors, setEditors] = useState([])
  const [approvers, setApprovers] = useState([])
  const [viewers, setViewers] = useState([])
  const [validationIssues, setValidationIssues] = useState([])

  // User selection modal state
  const [userSelectionVisible, setUserSelectionVisible] = useState(false)
  const [userSelectionType, setUserSelectionType] = useState('') // 'editor', 'approver', 'viewer'
  const [selectedUsers, setSelectedUsers] = useState([])
  const [availableUsers, setAvailableUsers] = useState([])

  const [stageModalVisible, setStageModalVisible] = useState(false)
  const [stageComment, setStageComment] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [currentStageAction, setCurrentStageAction] = useState(null)

  console.log('orgUserList CollaboraEditorModal: ', orgUserList)

  const stageConfigs = {
    approval: {
      title: 'Request Approval',
      placeholder: 'Enter your comment for the approval request...',
      okText: 'Submit Request',
      successMessage: 'Approval request submitted successfully'
    },
    draft: {
      title: 'Send Back to Draft',
      placeholder: 'Enter reason for rejection...',
      okText: 'Send Back',
      successMessage: 'Document sent back to draft successfully'
    },
    archived: {
      title: 'Approve Document',
      placeholder: 'Enter approval comment...',
      okText: 'Approve',
      successMessage: 'Document approved successfully'
    }
  }

  const sampleUsers = [
    {
      id: 1,
      name: 'Mahesh Joshi',
      email: 'mahesh@sharelookapp.com',
      avatar: null
    },
    {
      id: 2,
      name: 'Shikshya Dahal',
      email: 'shikshya@sharelookapp.com',
      avatar: null
    },
    {
      id: 3,
      name: 'Prabin Acharya',
      email: 'prabin@sharelookapp.com',
      avatar: null
    },
    {
      id: 4,
      name: 'Kismat Garthi Magar',
      email: 'kismat@sharelookapp.com',
      avatar: null
    },
    { id: 5, name: 'Kyi Phyu', email: 'kyi@sharelookapp.com', avatar: null },
    {
      id: 6,
      name: 'Robert Maina',
      email: 'robert@sharelookapp.com',
      avatar: null
    }
  ]

  const closeDocModal = async () => {
    console.log('DocumentManager: Closing modal')
    if (onClose) {
      onClose()
    }
    const token = await fetchAccessToken()
    // Save document version before closing
    try {
      const saveUrl = `${BASE_URL_COLLABORA}/api/documents/${documentId}/save-version`
      console.log('Saving document version...')
      const response = await axios.post(
        saveUrl,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        }
      )
      console.log('Document version saved successfully:', response.data)
    } catch (error) {
      console.error('Error saving document version:', error)
      // Don't block modal closing if save fails
    }

    // Call the original onClose function
  }

  // Workflow functions

  const openUserSelection__ = type => {
    setUserSelectionType(type)
    setSelectedUsers([])

    // Filter available users based on who's already assigned
    const allAssignedUsers = [...editors, ...approvers, ...viewers]
    const available = sampleUsers.filter(
      user => !allAssignedUsers.some(assigned => assigned.id === user.id)
    )
    setAvailableUsers(available)
    setUserSelectionVisible(true)
  }

  const openUserSelection = type => {
    setUserSelectionType(type)
    setSelectedUsers([])

    // Transform orgUserList to match the expected format
    const transformedUsers = orgUserList
      .filter(function (orgUser) {
        return orgUser && orgUser.user
      })
      .map(function (orgUser) {
        const user = orgUser.user
        return {
          id: user.id,
          name: user.name
            ? user.name
            : user.username
              ? user.username
              : 'Name N/A',
          email: user.email,
          avatar: user.avatar_id ? user.avatar_id : null
        }
      })

    // Filter available users based on who's already assigned
    // Add null checks for assigned users
    const allAssignedUsers = [...editors, ...approvers, ...viewers].filter(
      user => user !== null
    )
    const available = transformedUsers.filter(
      user =>
        !allAssignedUsers.some(assigned => assigned && assigned.id === user.id)
    )
    setAvailableUsers(available)
    setUserSelectionVisible(true)
  }

  const addEditor = () => {
    openUserSelection('editor')
  }

  const addApprover = () => {
    openUserSelection('approver')
  }

  const addViewer = () => {
    openUserSelection('viewer')
  }

  const handleStageUpdate = stage => {
    setCurrentStageAction(stage)
    setStageModalVisible(true)
  }

  const handleUserSelectionConfirm = () => {
    const usersToAdd = availableUsers.filter(user =>
      selectedUsers.includes(user.id)
    )

    if (userSelectionType === 'editor') {
      setEditors([...editors, ...usersToAdd])
    } else if (userSelectionType === 'approver') {
      setApprovers([...approvers, ...usersToAdd])
    } else if (userSelectionType === 'viewer') {
      setViewers([...viewers, ...usersToAdd])
    }

    setUserSelectionVisible(false)
    setSelectedUsers([])
    updateValidationIssues()
  }

  const handleUserSelectionCancel = () => {
    setUserSelectionVisible(false)
    setSelectedUsers([])
  }

  const removeEditor = async (id) => {
    const updatedEditors = editors.filter(editor => editor.id !== id)
    setEditors(updatedEditors)

    // Call update API immediately
    await updateWorkflowUsers(updatedEditors, approvers, viewers)
    updateValidationIssues()
  }

  const removeApprover = async (id) => {
    const updatedApprovers = approvers.filter(approver => approver.id !== id)
    setApprovers(updatedApprovers)

    // Call update API immediately
    await updateWorkflowUsers(editors, updatedApprovers, viewers)
    updateValidationIssues()
  }

  const removeViewer = async (id) => {
    const updatedViewers = viewers.filter(viewer => viewer.id !== id)
    setViewers(updatedViewers)

    // Call update API immediately
    await updateWorkflowUsers(editors, approvers, updatedViewers)
    updateValidationIssues()
  }

  // Add new helper function for update API call
  const updateWorkflowUsers = async (editorsToUpdate, approversToUpdate, viewersToUpdate) => {
    const workflowData = transformWorkflowData(editorsToUpdate, approversToUpdate, viewersToUpdate)
    const token = await fetchAccessToken()

    try {
      const response = await axios.post(
        `${BASE_URL_COLLABORA}/api/documents/${documentId}/update-workflow-users`,
        workflowData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      )

      message.success('User removed successfully')
      await getDocumentWorkflowInfo()
      return response.data
    } catch (error) {
      let errorMessage = 'An error occurred while updating workflow'

      if (
        error.response &&
        error.response.data &&
        error.response.data.data &&
        error.response.data.data.error
      ) {
        errorMessage = error.response.data.data.error
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message
      }

      message.error(errorMessage)
    }
  }

  const getUserSelectionTitle = () => {
    switch (userSelectionType) {
      case 'editor':
        return 'Add Editors'
      case 'approver':
        return 'Add Approvers'
      case 'viewer':
        return 'Add Viewers'
      default:
        return 'Select Users'
    }
  }

  const getUserSelectionDescription = () => {
    switch (userSelectionType) {
      case 'editor':
        return 'Select users who can edit this document'
      case 'approver':
        return 'Select users who can approve this document'
      case 'viewer':
        return 'Select users who can view this document'
      default:
        return 'Select users'
    }
  }
  const { t } = useTranslation()
  const renderUserSelectionModal = () => {
    return (
      <Modal
        title={getUserSelectionTitle()}
        visible={userSelectionVisible}
        onCancel={handleUserSelectionCancel}
        onOk={handleUserSelectionConfirm}
        okText={t('v5:add_selected_users')}
        cancelText={t('buttons:cancel')}
        width={500}
        okButtonProps={{
          disabled: selectedUsers.length === 0,
          type: 'primary'
        }}
      >
        <div style={{ marginBottom: '16px', color: '#666' }}>
          {getUserSelectionDescription()}
        </div>

        <Form layout="vertical">
          <Form.Item label="Select Users">
            <Select
              mode="multiple"
              placeholder={t('v5:choose_users_from_the_list')}
              value={selectedUsers}
              size="large"
              onChange={setSelectedUsers}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) => {
                const user = availableUsers.find(
                  u => u.id === option.props.value
                )
                return (
                  user &&
                  (user.name.toLowerCase().includes(input.toLowerCase()) ||
                    user.email.toLowerCase().includes(input.toLowerCase()))
                )
              }}
            >
              {availableUsers.map(user => (
                <Option key={user.id} value={user.id}>
                  <div
                    style={{ display: 'flex', alignItems: 'top', gap: '8px' }}
                  >
                    <Avatar
                      size="small"
                      style={{
                        backgroundColor: '#1890ff',
                        fontSize: '12px',
                        marginTop: '3px'
                      }}
                    >
                      {user.name.charAt(0)}
                    </Avatar>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: '14px' }}>
                        {user.name}
                      </div>
                      <div style={{ color: '#666', fontSize: '12px' }}>
                        {user.email}
                      </div>
                    </div>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>

        {selectedUsers.length > 0 && (
          <div style={{ marginTop: '16px' }}>
            <div
              style={{ marginBottom: '8px', fontWeight: 500, color: '#333' }}
            >
              {t('v5:selected_users')} ({selectedUsers.length}):
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {selectedUsers.map(userId => {
                const user = availableUsers.find(u => u.id === userId)
                return user ? (
                  <div
                    key={userId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}
                  >
                    <Avatar
                      size={16}
                      style={{ backgroundColor: '#1890ff', fontSize: '10px' }}
                    >
                      {user.name.charAt(0)}
                    </Avatar>
                    {user.name}
                  </div>
                ) : null
              })}
            </div>
          </div>
        )}
      </Modal>
    )
  }

  const updateValidationIssues = () => {
    const issues = []
    if (editors.length === 0) {
      issues.push('At least one editor is required')
    }
    if (approvers.length === 0) {
      issues.push('At least one approver is required')
    }
    setValidationIssues(issues)
  }

  const renderUserList = (users, onRemove, emptyMessage) => {
    if (users.length === 0) {
      return (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: '#999',
            backgroundColor: '#fafafa',
            border: '1px dashed #d9d9d9',
            borderRadius: '4px',
            margin: '8px 0'
          }}
        >
          {emptyMessage}
        </div>
      )
    }

    return (
      <div style={{ margin: '8px 0' }}>
        {users.map(user => (
          <Card
            key={user.id}
            size="small"
            style={{
              marginBottom: '8px',
              borderRadius: '6px'
            }}
            bodyStyle={{ padding: '12px' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Avatar
                  size="small"
                  style={{
                    backgroundColor: '#1890ff',
                    marginRight: '8px'
                  }}
                >
                  {user.name.charAt(0)}
                </Avatar>
                <div>
                  <div style={{ fontWeight: 500 }}>{user.name}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {user.email}
                  </div>
                </div>
              </div>
              <Button
                type="text"
                size="small"
                onClick={() => onRemove(user.id)}
                style={{ color: '#ff4d4f' }}
              >
                <FiX size={16} />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    )
  }

  const WorkflowSection = ({
    title,
    count,
    onAdd,
    users,
    onRemove,
    emptyMessage,
    addButtonText
  }) => (
    <div style={{ marginBottom: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}
      >
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
          {title} ({count})
        </h3>
        <Button
          type="primary"
          size="small"
          onClick={onAdd}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          <FiPlus style={{ marginRight: 2 }} />
          {addButtonText}
        </Button>
      </div>
      {renderUserList(users, onRemove, emptyMessage)}
    </div>
  )

  useEffect(() => {
    getDocumentWorkflowInfo()
    const initializeCollabora = async () => {
      try {
        setLoading(true)
        setError(null)
        setDebugInfo('Initializing Collabora...')

        // First, let's check if the Collabora service is accessible
        console.log('Checking Collabora service accessibility...')
        setDebugInfo('Checking Collabora service...')

        // Fetch the access token dynamically
        const token = await fetchAccessToken()
        if (!token) {
          setError('Failed to fetch access token')
          setLoading(false)
          return
        }

        // Generate WOPI URL
        const wopiSrc = encodeURIComponent(
          `${BASE_URL_COLLABORA}/wopi/files/${documentId}`
        )

        console.log('WOPI Source:', wopiSrc)
        console.log('Access Token:', token)
        console.log('Document Id:', documentId)

        // Try different Collabora URL formats
        const collaboraUrlOptions = [
          // Option 1: Standard Collabora URL
          `${BASE_URL_COLLABORA}/browser/dist/cool.html?WOPISrc=${wopiSrc}&access_token=${token}`,

          // Option 2: With service parameter
          `${BASE_URL_COLLABORA}?service=collabora&WOPISrc=${wopiSrc}&access_token=${token}`,

          // Option 3: Direct cool.html path
          `${BASE_URL_COLLABORA}/cool.html?WOPISrc=${wopiSrc}&access_token=${token}`,

          // Option 4: lool path (older Collabora versions)
          `${BASE_URL_COLLABORA}/lool/dist/lool.html?WOPISrc=${wopiSrc}&access_token=${token}`
        ]

        // Let's try the first option and log all possibilities
        const collaboraUrl = collaboraUrlOptions[0]
        console.log('Final Collabora URL:', collaboraUrl)
        console.log('Alternative URLs to try:', collaboraUrlOptions)

        setDebugInfo(`Loading URL: ${collaboraUrl}`)

        // Test if the WOPI endpoint is accessible
        try {
          const wopiTestUrl = `${BASE_URL_COLLABORA}/wopi/files/${documentId}?access_token=${token}`
          console.log('Testing WOPI endpoint:', wopiTestUrl)

          // WOPI CheckFileInfo call (GET request) - matching your curl command
          const response = await axios.get(wopiTestUrl, {
            headers: {
              Accept: 'application/json',
              'User-Agent': 'axios-client' // Some servers require a User-Agent
            },
            timeout: 10000
          })

          console.log('WOPI Response:', response.data)
          console.log('Response status:', response.status)
          setDebugInfo('WOPI endpoint accessible, loading editor...')
        } catch (wopiError) {
          console.error('WOPI endpoint test failed:', wopiError)
          console.error('Error details:', {
            status: wopiError.response && wopiError.response.status,
            statusText: wopiError.response && wopiError.response.statusText,
            data: wopiError.response && wopiError.response.data,
            headers: wopiError.response && wopiError.response.headers
          })
          setDebugInfo(
            `WOPI Error: ${wopiError.message} (Status: ${wopiError.response &&
            wopiError.response.status})`
          )

          // If it's a 401 or 403, it might be an authentication issue
          if (
            wopiError.response &&
            (wopiError.response.status === 401 ||
              wopiError.response.status === 403)
          ) {
            setError('Authentication failed - check your access token format')
            setLoading(false)
            return
          }

          // Continue anyway for other errors, the iframe might still work
        }

        // Load Collabora in iframe
        if (iframeRef.current) {
          iframeRef.current.src = collaboraUrl
          console.log('Iframe src set to:', collaboraUrl)
        }
      } catch (err) {
        console.error('Error initializing Collabora:', err)
        setError(`Failed to load Collabora editor: ${err.message}`)
        setDebugInfo(`Error: ${err.message}`)
        setLoading(false)
      }
    }

    if (visible) {
      initializeCollabora()
    }
  }, [visible, documentId])

  useEffect(() => {
    if (documentData && documentData.workflowInfo && documentData.workflowInfo.assignedUsers) {
      const { assignedUsers } = documentData.workflowInfo

      // Transform and set editors
      if (assignedUsers.editors && assignedUsers.editors.length > 0) {
        const transformedEditors = assignedUsers.editors.map(editor => ({
          id: parseInt(editor.userId),
          name: editor.userName || editor.email.split('@')[0],
          email: editor.email,
          avatar: null
        }))
        setEditors(transformedEditors)
      }

      // Transform and set approvers
      if (assignedUsers.approvers && assignedUsers.approvers.length > 0) {
        const transformedApprovers = assignedUsers.approvers.map(approver => ({
          id: parseInt(approver.userId),
          name: approver.userName || approver.email.split('@')[0],
          email: approver.email,
          avatar: null
        }))
        setApprovers(transformedApprovers)
      }

      // Transform and set viewers
      if (assignedUsers.viewers && assignedUsers.viewers.length > 0) {
        const transformedViewers = assignedUsers.viewers.map(viewer => ({
          id: parseInt(viewer.userId),
          name: viewer.userName || viewer.email.split('@')[0],
          email: viewer.email,
          avatar: null
        }))
        setViewers(transformedViewers)
      }
    }
  }, [documentData])

  const fetchAccessToken = async () => {
    try {
      console.log('Fetching access token...', isView, isEdit)
      setDebugInfo('Fetching access token...')

      const shlToken2 = ''

      // Make API call to get token
      const response = await axios.post(
        `${BASE_URL_COLLABORA}/api/documents/${documentId}/token`,
        {
          userId: String(userId),
          userName: userName,
          permissions:
            isView && !isEdit
              ? JSON.stringify(['view'])
              : JSON.stringify(['edit', 'view'])
        },
        {
          headers: {
            Authorization: `Bearer ${shlToken2}`,
            Accept: 'application/json'
          },
          timeout: 10000
        }
      )

      console.log('Token API Response:', response.data)

      // Extract token from response - adjust this based on your API response structure
      // You might need to modify this based on what your API returns
      const token = response.data.accessToken

      if (!token) {
        throw new Error('No token found in API response')
      }

      console.log('Fetched token:', token)
      setDebugInfo('Token fetched successfully')
      return token
    } catch (error) {
      console.error('Error fetching access token:', error)
      console.error('Error details:', {
        status: error.response && error.response.status,
        statusText: error.response && error.response.statusText,
        data: error.response && error.response.data,
        headers: error.response && error.response.headers
      })

      setDebugInfo(`Token fetch error: ${error.message}`)

      // Fallback to hardcoded token if API fails (for development)
      console.log('Falling back to hardcoded token...')
      const fallbackToken =
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJmaWxlSWQiOiIwNDljNzYyNS0wMTM3LTQ0ZDgtOWUxNi0xYTc3MGVlZWI1NTkuZG9jeCIsInVzZXJJZCI6InVzZXIxMjMiLCJ1c2VyTmFtZSI6IkRlZmF1bHQgVXNlciIsInBlcm1pc3Npb25zIjpbImVkaXQiLCJ2aWV3Il0sImV4cCI6MTc1MTcxMTg2N30.zg8Xh4doZfrrlBpX5WzMEA25RTL79Tz54FJCfBzmyd4'
      setDebugInfo('Using fallback token')
      return fallbackToken
    }
  }

  const handleIframeLoad = () => {
    console.log('Iframe loaded successfully')
    setTimeout(() => {
      setLoading(false)
      setDebugInfo('Editor loaded successfully')

      // Hide Collabora welcome dialog
      try {
        const iframe = iframeRef.current
        if (iframe && iframe.contentWindow && iframe.contentWindow.document) {
          const dialog = iframe.contentWindow.document.querySelector('.vex-dialog-form')
          if (dialog) {
            dialog.style.display = 'none'
          }
        }
      } catch (error) {
        console.log('Could not hide welcome dialog (cross-origin restriction):', error)
      }
    }, 3000) // 3000 milliseconds = 3 seconds
    setDebugInfo('Editor loaded successfully')
  }

  const handleIframeError = event => {
    console.error('Iframe error:', event)
    setError('Failed to load Collabora editor - iframe error')
    setLoading(false)
    setDebugInfo('Iframe failed to load')
  }

  const handleRetry = async () => {
    console.log('Retrying Collabora initialization...')
    setError(null)
    setLoading(true)
    setDebugInfo('Retrying...')

    if (iframeRef.current) {
      iframeRef.current.src = ''

      // Small delay before retry
      setTimeout(async () => {
        try {
          const token = await fetchAccessToken()
          const wopiSrc = encodeURIComponent(
            `${BASE_URL_COLLABORA}/wopi/files/${documentId}`
          )
          const collaboraUrl = `${BASE_URL_COLLABORA}/browser/dist/cool.html?WOPISrc=${wopiSrc}&access_token=${token}`

          console.log('Retry URL:', collaboraUrl)
          iframeRef.current.src = collaboraUrl
        } catch (error) {
          console.error('Retry failed:', error)
          setError(`Retry failed: ${error.message}`)
          setLoading(false)
        }
      }, 1000)
    }
  }

  const renderContent = () => {
    if (error) {
      return (
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            color: '#ff4d4f',
            height: '70vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Icon
            type="exclamation-circle"
            style={{ fontSize: '48px', marginBottom: '16px' }}
          />
          <h3>{t('v4:error_Loading_editor')}</h3>
          <p>{error}</p>
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}
          >
            <small>
              {t('v4:debug_info')}: {debugInfo}
            </small>
          </div>
          <Button
            type="primary"
            onClick={handleRetry}
            style={{ marginTop: '16px' }}
          >
            <Icon type="reload" />
            {t('v4:retry')}
          </Button>
        </div>
      )
    }

    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255,255,255,0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              flexDirection: 'column'
            }}
          >
            <Icon
              type="loading"
              style={{ fontSize: '24px', marginBottom: '16px' }}
            />
            <div>{t('v4:loading_editor')}</div>
            {/* <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
              {debugInfo}
            </div> */}
          </div>
        )}

        <iframe
          ref={iframeRef}
          width="100%"
          height="100%"
          frameBorder="0"
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          title="Editor"
          style={{ border: 'none' }}
          // Add sandbox attributes for security
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals"
        />
      </div>
    )
  }

  const handleRequestApproval = () => {
    handleStageUpdate('approval')
  }

  const handleApprove = () => {
    handleStageUpdate('archived')
  }

  const handleReject = () => {
    handleStageUpdate('draft')
  }

  const submitStageUpdate = async () => {
    if (!stageComment.trim()) {
      message.error('Please enter a comment')
      return
    }

    setSubmitting(true)

    try {
      const token = await fetchAccessToken()
      const response = await axios.post(
        `${BASE_URL_COLLABORA}/api/documents/${documentId}/update-stage`,
        {
          stage: currentStageAction,
          comment: stageComment.trim()
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}` // Replace with your actual token
          }
        }
      )

      const config = stageConfigs[currentStageAction]
      message.success(config.successMessage)
      setStageModalVisible(false)
      setStageComment('')
      setCurrentStageAction(null)
      getDocumentWorkflowInfo()

      // Add any additional logic here (e.g., refresh data, update state)
    } catch (error) {
      console.error('Error updating stage:', error)
      message.error('Failed to update document stage')
    } finally {
      setSubmitting(false)
    }
  }

  const handleModalCancel = () => {
    setStageModalVisible(false)
    setStageComment('')
    setCurrentStageAction(null)
  }

  const userRole =
    documentData && documentData.userContext && documentData.userContext.role

  console.log('User role:', userRole)

  const isEditorOrCreator =
    userRole === 'editor' ||
    userRole === 'creator' ||
    userRole === 'creator_approver'

  const isApprover = userRole === 'approver' || userRole === 'creator_approver'

  // Separate logic for archived status
  const isArchived =
    documentData &&
    Object.keys(documentData).length > 0 &&
    documentData.workflowInfo &&
    documentData.workflowInfo.stage === 'archived'

  // Separate logic for approval stage
  const isInApprovalStage =
    documentData &&
    documentData.workflowInfo &&
    documentData.workflowInfo.stage === 'approval'

  // Separate logic for draft stage
  const isInDraftStage =
    documentData &&
    documentData.workflowInfo &&
    documentData.workflowInfo.stage === 'draft'

  console.log('Is editor or creator:', isEditorOrCreator)
  console.log('Is approver:', isApprover)
  console.log('Is archived:', isArchived)
  console.log('Is in approval stage:', isInApprovalStage)
  console.log('Is in draft stage:', isInDraftStage)

  const header = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between', // pushes button to the far right
        width: '100%'
      }}
    >
      {/* left‑hand side = file icon + title */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Icon type="file-text" style={{ marginRight: 8 }} />
        {title}
      </div>

      {/* right‑hand side = Accept/Reject buttons + Workflow button */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isArchived ? (
          <Button type="primary" style={{ marginRight: '1em' }}>
            {t('v5:archived')}
          </Button>
        ) : (
          <>
            {isInDraftStage && isEditorOrCreator && (
              <button
                style={{
                  padding: '6px 16px',
                  background: '#1976d2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginRight: '1em'
                }}
                onClick={handleRequestApproval}
              >
                {t('v5:request_approval')}
              </button>
            )}

            {isInApprovalStage && (
              <>
                {userRole === 'editor' && (
                  <span
                    style={{
                      padding: '6px 16px',
                      background: '#fff3cd',
                      color: '#856404',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      marginRight: '1em'
                    }}
                  >
                    {t('v5:document_in_review')}
                  </span>
                )}
                {isApprover && (
                  <>
                    <button
                      onClick={handleApprove}
                      style={{
                        padding: '6px 16px',
                        background: '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        marginRight: '1em'
                      }}
                    >
                      {t('v5:approve')}
                    </button>
                    <button
                      onClick={handleReject}
                      style={{
                        padding: '6px 16px',
                        background: '#ff4d4f',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        marginRight: '1em'
                      }}
                    >
                      {t('v5:reject')}
                    </button>
                  </>
                )}
              </>
            )}

            {!isApprover && isInApprovalStage && (
              <div
                style={{
                  padding: '6px 16px',
                  background: '#e6f7ff',
                  color: '#1976d2',
                  borderRadius: '4px',
                  fontWeight: 'bold',
                  marginRight: '1em',
                  display: 'inline-block'
                }}
              >
                {t('v4:submitted_for_review')}
              </div>
            )}
          </>
        )}

        {isEdit && (
          <Button
            type="primary"
            style={{ marginRight: '2em' }}
            onClick={() => setWorkflowVisible(true)}
          >
            <FiActivity style={{ marginRight: 2 }} />
            {t('v4:workflow_management')}
          </Button>
        )}
      </div>
    </div>
  )

  const workflowHeader = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Icon type="setting" style={{ color: '#1890ff' }} />
      <span>{t('v4:workflow_management')}</span>
    </div>
  )

  const transformWorkflowData = (editors, approvers, viewers) => {
    const transformUser = user => {
      // Extract userName, with a fallback to the local part of the e‑mail.
      let userName = user && user.username ? user.username.trim() : ''

      if (!userName) {
        const email = user && user.email ? String(user.email) : ''
        userName = email.includes('@') ? email.split('@')[0] : '' // “furkan@…” → “furkan”
      }

      return {
        userId: String(user && user.id), // always a string
        userName, // never null
        email: user && user.email ? user.email : null
      }
    }

    return {
      editors: editors && editors.length > 0 ? editors.map(transformUser) : [],
      approvers:
        approvers && approvers.length > 0 ? approvers.map(transformUser) : [],
      viewers: viewers && viewers.length > 0 ? viewers.map(transformUser) : [],
      file_title: documentName
    }
  }

  const getDocumentWorkflowInfo = async () => {
    const token = await fetchAccessToken()
    const response = await axios.get(
      `${BASE_URL_COLLABORA}/api/documents/${documentId}/workflow-info`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    )
    console.log('response from GET workflow', response)
    setDocumentData(response.data)
  }

  const setWorkflowSave = async () => {
    console.log('editors: ', editors)
    console.log('approvers: ', approvers)
    console.log('viewers: ', viewers)
    console.log('clicked save')

    const workflowData = transformWorkflowData(editors, approvers, viewers)
    const token = await fetchAccessToken()

    // Check if this is an update (workflow already exists) or new workflow
    const isUpdate = documentData &&
      documentData.workflowInfo &&
      documentData.workflowInfo.assignedUsers &&
      (documentData.workflowInfo.assignedUsers.editors.length > 0 ||
        documentData.workflowInfo.assignedUsers.approvers.length > 0 ||
        documentData.workflowInfo.assignedUsers.viewers.length > 0)

    const apiEndpoint = isUpdate
      ? `${BASE_URL_COLLABORA}/api/documents/${documentId}/update-workflow-users`
      : `${BASE_URL_COLLABORA}/api/documents/${documentId}/add-workflow`

    try {
      const response = await axios.post(
        apiEndpoint,
        workflowData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      )
      console.log('response from workflow api: ', response)

      message.success(isUpdate ? 'Workflow updated successfully' : 'Workflow added successfully')
      setWorkflowVisible(false)

      // Refresh the workflow info to get the latest state
      await getDocumentWorkflowInfo()

      return response.data
    } catch (error) {
      console.log(
        'error.response.data ',
        error.response &&
        error.response.data &&
        error.response.data.data &&
        error.response.data.data.error
      )

      let errorMessage = 'An error occurred while ' + (isUpdate ? 'updating' : 'adding') + ' workflow'

      if (
        error.response &&
        error.response.data &&
        error.response.data.data &&
        error.response.data.data.error
      ) {
        errorMessage = error.response.data.data.error
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error
      ) {
        errorMessage = error.response.data.error
      } else if (error.response) {
        errorMessage = 'Server error: ' + error.response.status
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server'
      } else {
        errorMessage = error.message || 'Unknown error occurred'
      }

      message.error(errorMessage)
    }
  }

  const workflowFooter = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0 0 0'
      }}
    >
      <Button onClick={() => setWorkflowVisible(false)}>{t('v3:close')}</Button>
      <Button
        type="primary"
        danger
        style={{ backgroundColor: '#faad14', borderColor: '#faad14', display: 'flex', alignItems: 'center' }}
        onClick={() => setWorkflowSave()}
      >
        <FiSave style={{ marginRight: '4px' }} />
        <span>
          {documentData &&
            documentData.workflowInfo &&
            documentData.workflowInfo.assignedUsers &&
            (documentData.workflowInfo.assignedUsers.editors.length > 0 ||
              documentData.workflowInfo.assignedUsers.approvers.length > 0 ||
              documentData.workflowInfo.assignedUsers.viewers.length > 0)
            ? t('buttons:update') || 'Update'
            : t('buttons:save')}
        </span>
      </Button>
    </div>
  )

  return (
    <>
      <Modal
        title={
          currentStageAction
            ? stageConfigs[currentStageAction].title
            : 'Update Stage'
        }
        visible={stageModalVisible}
        onOk={submitStageUpdate}
        onCancel={handleModalCancel}
        okText={
          currentStageAction
            ? stageConfigs[currentStageAction].okText
            : 'Submit'
        }
        cancelText={t('v4:cancel')}
        confirmLoading={submitting}
      >
        <div style={{ marginBottom: '16px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold'
            }}
          >
            {t('v5:comment')}
          </label>
          <TextArea
            value={stageComment}
            onChange={e => setStageComment(e.target.value)}
            placeholder={
              currentStageAction
                ? stageConfigs[currentStageAction].placeholder
                : t('v5:enter_your_commit')
            }
            rows={4}
            maxLength={500}
          />
        </div>
      </Modal>
      <Modal
        title={header}
        visible={visible}
        onCancel={closeDocModal}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        bodyStyle={{
          padding: 0,
          height: '85vh',
          overflow: 'hidden'
        }}
        destroyOnClose={true}
        maskClosable={false}
        keyboard={false}
        centered={false}
      >
        {renderContent()}
      </Modal>

      {/* USER SELECTION MODAL */}
      {renderUserSelectionModal()}

      {/* WORKFLOW MODAL */}
      <Modal
        title={workflowHeader}
        visible={workflowVisible}
        onCancel={() => setWorkflowVisible(false)}
        footer={workflowFooter}
        width={600}
        destroyOnClose
        centered
        bodyStyle={{ padding: '20px' }}
      >
        <div style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
          {t('v4:configure_editors_approvers_and_viewers_for_this_document')}
        </div>

        <WorkflowSection
          title={t('v4:editors')}
          count={editors.length}
          onAdd={addEditor}
          users={editors}
          onRemove={removeEditor}
          emptyMessage={t('v4:no_editors_added_yet')}
          addButtonText={t('v4:add_editor')}
        />

        <WorkflowSection
          title={t('v4:approvers')}
          count={approvers.length}
          onAdd={addApprover}
          users={approvers}
          onRemove={removeApprover}
          emptyMessage={t('v4:no_approvers_added_yet')}
          addButtonText={t('v4:add_approvers')}
        />

        <WorkflowSection
          title={t('v4:viewer')}
          count={viewers.length}
          onAdd={addViewer}
          users={viewers}
          onRemove={removeViewer}
          emptyMessage={t('v4:no_viewers_added_yet')}
          addButtonText={t('v4:add_viewer')}
        />
      </Modal>
    </>
  )
}

const DocumentManager = ({
  orgUserList = [],
  isDocOpened = false,
  documentName,
  documentId,
  userId,
  userName,
  closeModal,
  shlToken,
  isEdit = false,
  isView = false,
  orgId, // ADD THIS
}) => {
  console.log('List orgUserList', orgUserList)
  console.log('Log documentName', documentName)
  console.log('Log documentId', documentId)
  const closeDocModal = () => {
    console.log('DocumentManager: Closing modal')
    if (closeModal) {
      closeModal(false)
    }
  }

  return (
    <CollaboraEditorModal
      orgUserList={orgUserList}
      visible={isDocOpened}
      onClose={closeDocModal}
      documentName={documentName}
      documentId={documentId}
      userId={userId}
      userName={userName}
      shlToken={shlToken}
      title={`Editing: ${documentName}`}
      isEdit={isEdit}
      isView={isView}
      orgId={orgId} // ADD THIS
    />
  )
}

export default DocumentManager
